<script setup>
import {computed, ref, watch} from 'vue';
import vSelect from 'vue-select';
import { keepEscapeInsideOpenSelect } from '@/utils/vueSelect.js';
import {useUnsavedChanges, unsavedChangesSnapshot} from '@/composables/useUnsavedChanges.js';

const props = defineProps({
    leadId: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits(['update:lead-company']);

const leadCompany = ref(null);
const workingState = ref('');
const unlinkState = ref('');
const selectionMode = ref(null);
const existingLeadCompanies = ref([]);
const selectedLeadCompanyId = ref(null);
let savedSnapshot = null;

const hasLinkedLeadCompany = computed(() => leadCompany.value?.id !== null);

useUnsavedChanges(() => {
    if (!leadCompany.value || hasLinkedLeadCompany.value) {
        return false;
    }

    return (selectionMode.value === 'assign' && selectedLeadCompanyId.value !== null)
        || (selectionMode.value === 'create' && unsavedChangesSnapshot(leadCompany.value) !== savedSnapshot);
});

const loadLeadCompany = async () => {
    const res = await axios.get(route('api.crm.lead.company.show', props.leadId));
    leadCompany.value = res.data.lead_company;
    savedSnapshot = unsavedChangesSnapshot(leadCompany.value);
    selectionMode.value = null;
    selectedLeadCompanyId.value = null;
    existingLeadCompanies.value = [];
};

const loadExistingLeadCompanies = async (query = '') => {
    const res = await axios.get(route('api.crm.lead.company.options', props.leadId), {
        params: {query},
    });

    existingLeadCompanies.value = res.data.lead_companies.map((leadCompanyOption) => ({
        ...leadCompanyOption,
        label: [
            leadCompanyOption.name,
            leadCompanyOption.org_nr,
            leadCompanyOption.invoice_email,
        ].filter(Boolean).join(' • '),
    }));
};

const searchExistingLeadCompanies = async (search, loading) => {
    loading(true);

    try {
        await loadExistingLeadCompanies(search);
    } finally {
        loading(false);
    }
};

const setSelectionMode = async (mode) => {
    selectionMode.value = selectionMode.value === mode ? null : mode;
    workingState.value = '';

    if (mode === 'assign') {
        selectedLeadCompanyId.value = null;
        await loadExistingLeadCompanies();
    }
};

const assignLeadCompany = async () => {
    if (!selectedLeadCompanyId.value) {
        return;
    }

    workingState.value = 'working';

    try {
        const res = await axios.put(route('api.crm.lead.company.assign', props.leadId), {
            lead_company_id: selectedLeadCompanyId.value,
        });

        leadCompany.value = res.data.lead_company;
        workingState.value = 'success';
        emit('update:lead-company', leadCompany.value);
    } catch (error) {
        workingState.value = 'error';
    }
};

const unlinkLeadCompany = async () => {
    if (!leadCompany.value?.id) {
        return;
    }

    unlinkState.value = 'waiting';

    try {
        await axios.delete(route('bo.api.v1.crm.lead-company.lead.disconnect', {
            id: leadCompany.value.id,
            leadId: props.leadId,
        }));

        await loadLeadCompany();
        unlinkState.value = '';
        emit('update:lead-company', leadCompany.value);
    } catch (error) {
        unlinkState.value = 'error';
    }
};

const createLeadCompany = async () => {
    workingState.value = 'working';

    try {
        const res = await axios.put(route('api.crm.lead.company.update', props.leadId), leadCompany.value);
        leadCompany.value = res.data.lead_company;
        workingState.value = 'success';
        emit('update:lead-company', leadCompany.value);
    } catch (error) {
        workingState.value = 'error';
    }
};

watch(() => props.leadId, () => {
    if (props.leadId) {
        loadLeadCompany();
    }
}, {immediate: true});
</script>

<template>
    <div v-if="leadCompany">
        <card-default class="mt-4 overflow-visible">
            <template #title>
                <h3 v-if="hasLinkedLeadCompany">{{ _mft('crm:company') }}</h3>
                <h3 v-else>{{ _mft('crm:leadCompany.leadCompany') }}</h3>
            </template>
            <template #topRight>
                <div
                    v-if="hasLinkedLeadCompany"
                    class="flex items-center gap-2"
                >
                    <button-with-confirmation
                        size="sm"
                        style-type="danger"
                        confirm-type="danger"
                        :state="unlinkState"
                        :title="_mft('crm:leadCompany.unlinkFromCompany')"
                        :confirm-text="_mft('crm:leadCompany.unlinkFromCompanyConfirm', { name: leadCompany.name })"
                        :confirm-button-label="_mft('crm:leadCompany.unlinkFromCompany')"
                        @confirmed="unlinkLeadCompany"
                    >
                        <i class="fa-light fa-link-slash"></i>
                    </button-with-confirmation>
                    <a
                        :href="route('crm.lead-company.single', leadCompany.id)"
                    >
                        <button-secondary size="sm">
                            {{ _mft('shared:action.edit') }}
                        </button-secondary>
                    </a>
                </div>
            </template>
            <div class="@container">
                <div v-if="hasLinkedLeadCompany">
                    <div class="grid gap-4 grid-cols-1">
                        <div>
                            <input-label :label="_mft('crm:contact.company.fields.name')" />
                            <p class="mb-0">
                                {{ leadCompany.name || '-' }}
                            </p>
                        </div>

                        <div>
                            <input-label :label="_mft('crm:contact.company.fields.corporateIdentityNumber')" />
                            <p class="mb-0">
                                {{ leadCompany.org_nr || '-' }}
                            </p>
                        </div>

                        <div>
                            <input-label :label="_mft('crm:contact.company.fields.invoiceEmail')" />
                            <p class="mb-0">
                                {{ leadCompany.invoice_email || '-' }}
                            </p>
                        </div>

                        <div>
                            <input-label :label="_mft('crm:contact.company.fields.employees')" />
                            <p class="mb-0">
                                {{ leadCompany.employees ?? '-' }}
                            </p>
                        </div>

                        <div>
                            <input-label :label="_mft('crm:leadCompany.billingDetails')" />
                            <p class="mb-0">
                                {{
                                    [
                                        leadCompany.billing_street_address,
                                        leadCompany.billing_zipcode,
                                        leadCompany.billing_city,
                                        leadCompany.billing_country,
                                    ].filter(Boolean).join(', ') || '-'
                                }}
                            </p>
                        </div>

                        <div v-if="leadCompany.description">
                            <input-label :label="_mft('crm:contact.company.fields.description')" />
                            <p class="mb-0 whitespace-pre-line break-words">{{ leadCompany.description }}</p>
                        </div>
                    </div>
                </div>

                <div v-else>
                    <div>
                        <div
                            v-if="selectionMode === null"
                        >
                            <div class="mx-auto rounded-full bg-gray-100 w-12 h-12 flex justify-center items-center self-center mb-2">
                                <i class="fa-light fa-building text-gray-400 text-2xl"></i>
                            </div>
                            <p class="text-center text-xs text-gray-600">{{ _mft('crm:leadCompany.notLinkedYet') }}</p>
                            <div
                                class="mb-6 flex flex-col gap-2 items-center"
                            >
                                <button-base
                                    type="button"
                                    size="sm"
                                    :style-type="selectionMode === 'assign' ? 'tinted' : 'primary'"
                                    @click="setSelectionMode('assign')"
                                >
                                    {{ _mft('crm:leadCompany.assignExistingCompany') }}
                                </button-base>

                                <button-base
                                    type="button"
                                    size="sm"
                                    :style-type="selectionMode === 'create' ? 'tinted' : 'transparent'"
                                    @click="setSelectionMode('create')"
                                >
                                    {{ _mft('crm:leadCompany.createNewCompany') }}
                                </button-base>
                            </div>
                        </div>

                        <div v-if="selectionMode === 'assign'">
                            <input-label
                                class="mb-1"
                                :label="_mft('crm:leadCompany.existingLeadCompany')"
                            />

                            <v-select
                                :map-keydown="keepEscapeInsideOpenSelect"
                                :options="existingLeadCompanies"
                                :reduce="(option) => option.id"
                                label="label"
                                v-model="selectedLeadCompanyId"
                                :filterable="false"
                                @search="searchExistingLeadCompanies"
                            >
                                <template #search="{attributes, events}">
                                    <input
                                        class="vs__search"
                                        :placeholder="_mft('crm:leadCompany.searchByCompanyNameOrgNumberOrInvoiceEmail')"
                                        v-bind="attributes"
                                        v-on="events"
                                    />
                                </template>
                            </v-select>

                            <div class="mt-4 flex justify-end space-x-2">
                                <button-secondary
                                    type="button"
                                    @click="setSelectionMode(null)"
                                >
                                    {{ _mft('shared:action.cancel') }}
                                </button-secondary>
                                <button-success
                                    type="button"
                                    @click="assignLeadCompany"
                                    :state="workingState"
                                    :disabled="!selectedLeadCompanyId"
                                >
                                    {{ _mft('crm:leadCompany.assignCompany') }}
                                </button-success>
                            </div>
                        </div>

                        <div v-if="selectionMode === 'create'">
                            <div class="grid-cols-4 gap-x-8 @md:grid @sm:grid-cols-2 @lg:grid-cols-4">
                                <div class="col-span-2">
                                    <input-text
                                        auto-complete="off"
                                        maxlength="255"
                                        name="lead_company_name"
                                        input-id="lead_company_name"
                                        :label="_mft('crm:contact.company.fields.name')"
                                        v-model:content="leadCompany.name"
                                    />

                                    <input-text
                                        auto-complete="off"
                                        maxlength="255"
                                        name="lead_company_org_nr"
                                        input-id="lead_company_org_nr"
                                        :label="_mft('crm:contact.company.fields.corporateIdentityNumber')"
                                        v-model:content="leadCompany.org_nr"
                                    />

                                    <input-text
                                        auto-complete="off"
                                        maxlength="255"
                                        name="lead_company_invoice_email"
                                        input-id="lead_company_invoice_email"
                                        :label="_mft('crm:contact.company.fields.invoiceEmail')"
                                        v-model:content="leadCompany.invoice_email"
                                    />

                                    <input-number
                                        name="lead_company_employees"
                                        input-id="lead_company_employees"
                                        v-model:content="leadCompany.employees"
                                        :label="_mft('crm:contact.company.fields.employees')"
                                    />
                                </div>

                                <div class="col-span-2">
                                    <div class="@md:grid @md:grid-cols-2 @md:gap-4">
                                        <input-text
                                            auto-complete="off"
                                            maxlength="255"
                                            name="lead_company_billing_street_address"
                                            input-id="lead_company_billing_street_address"
                                            :label="_mft('crm:contact.company.fields.billingStreetAddress')"
                                            v-model:content="leadCompany.billing_street_address"
                                        />

                                        <input-text
                                            auto-complete="off"
                                            maxlength="255"
                                            name="lead_company_billing_zipcode"
                                            input-id="lead_company_billing_zipcode"
                                            :label="_mft('crm:contact.company.fields.billingZipcode')"
                                            v-model:content="leadCompany.billing_zipcode"
                                        />
                                    </div>

                                    <div class="@md:grid @md:grid-cols-2 @md:gap-4">
                                        <input-text
                                            auto-complete="off"
                                            maxlength="255"
                                            name="lead_company_billing_city"
                                            input-id="lead_company_billing_city"
                                            :label="_mft('crm:contact.company.fields.billingCity')"
                                            v-model:content="leadCompany.billing_city"
                                        />

                                        <input-language
                                            :label="_mft('crm:contact.company.fields.billingCountry')"
                                            name="lead_company_country"
                                            v-model:country="leadCompany.billing_country"
                                            @selected="leadCompany.billing_country = $event.label"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div class="mt-4 flex justify-end space-x-2">
                                <button-secondary
                                    type="button"
                                    @click="setSelectionMode(null)"
                                >
                                    {{ _mft('shared:action.cancel') }}
                                </button-secondary>
                                <button-success
                                    @click="createLeadCompany"
                                    :state="workingState"
                                >
                                    {{ _mft('crm:leadCompany.createCompany') }}
                                </button-success>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </card-default>
    </div>
</template>
